import { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, MoreHorizontal, Pencil } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useServerPagination } from '@/hooks/useServerPagination';
import { ListPagination } from '@/components/ListPagination';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { useTableSort } from '@/hooks/useTableSort';
import { Button } from '@/components/ui/button';
import AddMaintenanceSheet from '@/components/contracts/AddMaintenanceSheet';

const ITEMS_PER_PAGE = 10;

type MaintenanceType = 'conversion' | 'refinement' | 'servicing' | 'warranty' | 'insurance' | 'refurbishment';

export default function MaintenanceRecords() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [editMaintenanceId, setEditMaintenanceId] = useState<number | null>(null);
  const [editVehicleId, setEditVehicleId] = useState<number | null>(null);

  const pagination = useServerPagination({
    totalCount: 0,
    itemsPerPage: ITEMS_PER_PAGE,
  });

  // Map UI sort keys to database column names
  // vehicleName is computed from joined data and can't be sorted server-side
  const getSortColumn = (key: string | null): string | null => {
    if (key === 'vehicleName') return null; // Can't sort by computed column
    return key || 'started_at';
  };

  const { sortKey, sortDirection, handleSort } = useTableSort({
    defaultSortKey: 'started_at',
    defaultSortDirection: 'desc',
    onSortChange: pagination.resetPage,
  });

  // Fetch all maintenances with relations for search filtering
  const { data: allMaintenancesForSearch } = useQuery({
    queryKey: ['maintenances-search-data'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vehicle_maintenances')
        .select(`
          id,
          description,
          vehicles (make, model, vehicle_registrations (number))
        `);
      if (error) throw error;
      return data;
    },
  });

  // Get filtered maintenance IDs based on search
  const getFilteredMaintenanceIds = () => {
    if (!allMaintenancesForSearch || !debouncedSearch) return null;

    const searchLower = debouncedSearch.toLowerCase();
    return allMaintenancesForSearch
      .filter(maint =>
        maint.vehicles?.make?.toLowerCase().includes(searchLower) ||
        maint.vehicles?.model?.toLowerCase().includes(searchLower) ||
        maint.description?.toLowerCase().includes(searchLower)
      )
      .map(maint => maint.id);
  };

  const filteredMaintenanceIds = getFilteredMaintenanceIds();

  const { data: countData } = useQuery({
    queryKey: ['maintenances-count', typeFilter, debouncedSearch, filteredMaintenanceIds],
    queryFn: async () => {
      if (filteredMaintenanceIds !== null && filteredMaintenanceIds.length === 0) {
        return 0;
      }

      let query = supabase.from('vehicle_maintenances').select('*', { count: 'exact', head: true });

      if (typeFilter !== 'all') {
        query = query.eq('type', typeFilter as MaintenanceType);
      }

      if (filteredMaintenanceIds !== null) {
        query = query.in('id', filteredMaintenanceIds);
      }

      const { count, error } = await query;
      if (error) throw error;
      return count || 0;
    },
    enabled: allMaintenancesForSearch !== undefined,
  });

  const paginationWithCount = useServerPagination({
    totalCount: countData || 0,
    itemsPerPage: ITEMS_PER_PAGE,
  });

  const { data: maintenances, isLoading } = useQuery({
    queryKey: ['maintenances', paginationWithCount.currentPage, sortKey, sortDirection, typeFilter, debouncedSearch, filteredMaintenanceIds],
    queryFn: async () => {
      if (filteredMaintenanceIds !== null && filteredMaintenanceIds.length === 0) {
        return [];
      }

      const sortColumn = getSortColumn(sortKey);
      let query = supabase
        .from('vehicle_maintenances')
        .select(`
          *,
          vehicles(make, model, vehicle_registrations(number)),
          transactions(amount),
          contracts(drivers(profiles(id, admin_id, first_name, last_name)))
        `);

      // Only apply server-side sort for valid database columns
      if (sortColumn) {
        query = query.order(sortColumn, { ascending: sortDirection === 'asc' });
      } else {
        query = query.order('started_at', { ascending: false });
      }

      if (typeFilter !== 'all') {
        query = query.eq('type', typeFilter as MaintenanceType);
      }

      if (filteredMaintenanceIds !== null) {
        query = query.in('id', filteredMaintenanceIds);
      }

      query = query.range(paginationWithCount.from, paginationWithCount.to);

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: countData !== undefined && allMaintenancesForSearch !== undefined,
  });

  const { data: maintenanceTypes } = useQuery({
    queryKey: ['maintenance-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vehicle_maintenances')
        .select('type');
      if (error) throw error;
      return [...new Set(data?.map(m => m.type).filter(Boolean) || [])].sort();
    },
  });

  useEffect(() => {
    paginationWithCount.resetPage();
  }, [typeFilter, debouncedSearch]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
  };

  const capitalizeFirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

  // Display maintenances (server-side sorted)
  const displayMaintenances = maintenances ?? [];

  return (
    <>
    <Card className="border-0">
      <CardHeader>
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search maintenance..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {maintenanceTypes?.map((type) => (
                <SelectItem key={type} value={type} className="capitalize">
                  {capitalizeFirst(type)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : (
          <>
            <ListPagination
              currentPage={paginationWithCount.currentPage}
              totalPages={paginationWithCount.totalPages}
              totalItems={paginationWithCount.totalItems}
              onPageChange={paginationWithCount.goToPage}
            />
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead>
                      Vehicle
                    </SortableTableHead>
                    <SortableTableHead
                      sortKey="type"
                      currentSortKey={sortKey}
                      currentSortDirection={sortDirection}
                      onSort={handleSort}
                    >
                      Type
                    </SortableTableHead>
                    <SortableTableHead
                      sortKey="description"
                      currentSortKey={sortKey}
                      currentSortDirection={sortDirection}
                      onSort={handleSort}
                    >
                      Description
                    </SortableTableHead>
                    <SortableTableHead
                      sortKey="started_at"
                      currentSortKey={sortKey}
                      currentSortDirection={sortDirection}
                      onSort={handleSort}
                    >
                      Date
                    </SortableTableHead>
                    <SortableTableHead
                      sortKey="amount"
                      currentSortKey={sortKey}
                      currentSortDirection={sortDirection}
                      onSort={handleSort}
                      align="right"
                    >
                      Cost
                    </SortableTableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayMaintenances.map((maint) => {
                    const vehicleReg = maint.vehicles?.vehicle_registrations;
                    const regNumber = Array.isArray(vehicleReg) ? vehicleReg[0]?.number : vehicleReg?.number;
                    return (
                      <TableRow key={maint.id}>
                        <TableCell>
                          <div>
                            <span className="font-medium">{regNumber || 'Unregistered'}</span>
                            <p className="text-xs text-muted-foreground">{maint.vehicles?.make} {maint.vehicles?.model}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {maint.type || 'N/A'}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {maint.description}
                        </TableCell>
                        <TableCell>
                          {new Date(maint.started_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {maint.transactions?.amount ? formatCurrency(maint.transactions.amount) : '-'}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => {
                                setEditVehicleId(maint.vehicle_id);
                                setEditMaintenanceId(maint.id);
                              }}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {displayMaintenances.length === 0 && !isLoading && (
                    <TableRow>
                      <TableCell colSpan={6} className="h-32 text-center text-muted-foreground">
                        No maintenance records found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>

    {editMaintenanceId && editVehicleId && (
      <AddMaintenanceSheet
        open={!!editMaintenanceId}
        onOpenChange={(open) => { if (!open) { setEditMaintenanceId(null); setEditVehicleId(null); } }}
        vehicleId={editVehicleId}
        maintenanceId={editMaintenanceId}
      />
    )}
    </>
  );
}
